import { supabase } from './supabase';

/** Has this account paid through Stripe (so the Customer Portal has something to show)? */
export const hasStripeBilling = (org) =>
  !!org?.stripe_customer_id && org.billing_status !== 'complimentary';

/** Opens the Stripe Customer Portal (change plan or seats, card, invoices, cancel). */
export async function openBillingPortal(orgId, returnPath = window.location.pathname + window.location.search) {
  const { data, error } = await supabase.functions.invoke('billing-portal', { body: { org_id: orgId, return_path: returnPath } });
  if (error) {
    let message = 'We could not open billing right now. Please try again.';
    try {
      const body = await error.context?.json?.();
      if (body?.error) message = body.error;
    } catch { /* keep generic */ }
    throw new Error(message);
  }
  window.location.assign(data.url);
}

/** Calls create-checkout and returns the Stripe URL, or throws with a readable message. */
export async function startCheckout(payload) {
  const { data, error } = await supabase.functions.invoke('create-checkout', { body: payload });
  if (error) {
    let message = 'We could not start checkout. Please try again.';
    let code;
    try {
      const body = await error.context?.json?.();
      if (body?.error) message = body.error;
      code = body?.code;
    } catch { /* keep generic */ }
    const err = new Error(message);
    err.code = code;
    throw err;
  }
  if (!data?.url) throw new Error('We could not start checkout. Please try again.');
  return data.url;
}

/**
 * What plan an account is on, for showing to its members.
 * tone: 'paid' | 'free' | 'warn'. upgradable = free, so an owner/admin can buy Pro (personal) or Teams (company).
 */
export function planStatus(org) {
  const paidPlan = org?.kind === 'company' ? 'Teams' : 'Pro';
  const named = org?.plan === 'teams' ? 'Teams' : org?.plan === 'pro' ? 'Pro' : org?.plan === 'enterprise' ? 'Enterprise' : paidPlan;
  if (org?.billing_status === 'complimentary') {
    const ended = org.comp_until && new Date(org.comp_until) < new Date();
    if (!ended) {
      // Founder offer: the owner's card is free; adding the team (Teams, 25% off) is the upgrade
      if (org.offer === 'team25' && org.kind === 'company') {
        const until = org.comp_until ? new Date(org.comp_until).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) : null;
        return { label: until ? `Free until ${until}` : 'Free access', tone: 'paid', upgradable: true, upgradeTo: 'teams', offer: 'team25' };
      }
      return { label: `${named} · free access`, tone: 'paid', upgradable: false };
    }
    return { label: 'Free plan', tone: 'free', upgradable: false }; // a Carduzo admin decides what happens next
  }
  if (org?.billing_status === 'trialing') return { label: `${named} · free trial`, tone: 'paid', upgradable: false };
  if (org?.billing_status === 'active') return { label: named, tone: 'paid', upgradable: false };
  if (org?.billing_status === 'past_due') return { label: `${named} · payment due`, tone: 'warn', upgradable: false };
  return { label: 'Free plan', tone: 'free', upgradable: true, upgradeTo: org?.kind === 'company' ? 'teams' : 'pro' };
}

/**
 * Same rule as private.org_has_access() in the database: the account pays (active,
 * trialing, or past_due while Stripe retries) or has unexpired free access from Carduzo.
 * Unlocks the lead form (Pro / Teams) and, for companies, branding, locks, Activity and invites (Teams).
 */
export function hasPaidAccess(org) {
  if (!org || org.status === 'suspended') return false;
  if (['active', 'trialing', 'past_due'].includes(org.billing_status)) return true;
  if (org.billing_status === 'complimentary') return !org.comp_until || new Date(org.comp_until) > new Date();
  return false;
}
